import { ChevronDownIcon, ChevronRightIcon, TrashCIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Text } from '@prophecy/ui-v3/Typography';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { memo, useState } from 'react';
import styled from 'styled-components';

import { PromptAttachments } from './ComposerAttachments';
import { HOME_CHAT_TEST_IDS } from './constants';

import type { QueuedHomeChatPrompt } from './useQueuedHomeChatPrompts';
import type { MouseEvent } from 'react';

/** Cursor-style queued prompt strip: bordered rows above the chat input, mirroring agent-v4's. */
const ListRoot = styled(Stack)`
  padding: ${theme.spaces.x4} 0;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.x4};
  padding: ${theme.spaces.x2} ${theme.spaces.x4};
  margin: 0 0 ${theme.spaces.x4};
  background: transparent;
  border: none;
  border-radius: ${theme.radius.x4};
  cursor: pointer;
  color: ${theme.colors.gray500};
  align-self: flex-start;
  &:hover {
    background: ${theme.colors.gray100};
    color: ${theme.colors.gray700};
  }
`;

const Row = styled(Stack)`
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x6};
  background: ${theme.colors.gray50};
  padding: ${theme.spaces.x6} ${theme.spaces.x8};
  &:hover {
    background: ${theme.colors.white};
    border-color: ${theme.colors.gray300};
  }
`;

const RowMain = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const RowText = styled(Text)`
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;
`;

/** Cap list height so a huge queue does not bury the thread or composer. */
const QueuedItemsScrollArea = styled(Stack)`
  max-height: min(40vh, ${theme.width.x320});
  overflow-y: auto;
  overflow-x: hidden;
`;

type QueuedHomeChatPromptsListProps = {
  queue: QueuedHomeChatPrompt[];
  onRemove: (id: string) => void;
};

function _QueuedHomeChatPromptsList({ queue, onRemove }: QueuedHomeChatPromptsListProps) {
  const [isOpen, setIsOpen] = useState(true);
  const toggleOpen = usePersistentCallback(() => setIsOpen((prev) => !prev));

  if (queue.length === 0) return null;

  return (
    <ListRoot direction='vertical' gap={theme.spaces.x4} data-test-id={HOME_CHAT_TEST_IDS.queuedPrompts}>
      <HeaderButton
        type='button'
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls='home-chat-queued-prompts-items'
        data-test-id={HOME_CHAT_TEST_IDS.queuedPromptsToggle}>
        {isOpen ? <ChevronDownIcon type='default' size='xs' /> : <ChevronRightIcon type='default' size='xs' />}
        <Text level='xs' tone={theme.colors.gray500}>
          {queue.length === 1 ? 'Queued (1)' : `Queued (${queue.length})`}
        </Text>
      </HeaderButton>
      {isOpen && (
        <QueuedItemsScrollArea id='home-chat-queued-prompts-items' direction='vertical' gap={theme.spaces.x4}>
          {queue.map((prompt) => (
            <Row
              key={prompt.id}
              direction='horizontal'
              alignY='center'
              gap={theme.spaces.x8}
              data-test-id={HOME_CHAT_TEST_IDS.queuedPromptRow}>
              <RowMain direction='vertical' gap={theme.spaces.x6}>
                <RowText level='sm' tone={theme.colors.gray700} title={prompt.text}>
                  {prompt.text}
                </RowText>
                <PromptAttachments context={prompt.context} />
              </RowMain>
              <Button
                size='xs'
                variant='tertiaryGray'
                shape='circle'
                icon={<TrashCIcon type='default' size='xs' />}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onRemove(prompt.id);
                }}
                aria-label='Remove queued prompt'
                data-test-id={HOME_CHAT_TEST_IDS.queuedPromptDelete}
              />
            </Row>
          ))}
        </QueuedItemsScrollArea>
      )}
    </ListRoot>
  );
}

export const QueuedHomeChatPromptsList = memo(_QueuedHomeChatPromptsList);
